import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { 
  ArrowLeftIcon,
  PlusIcon, 
  TrashIcon,
  MagnifyingGlassIcon,
  BuildingOfficeIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  XCircleIcon,
  CheckIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { useAppData } from '../../contexts/AppDataContext';
import { useDocumentTypes } from '../../contexts/DocumentTypesContext';
import { useDocuments } from '../../contexts/DocumentsContext';
import { v4 as uuidv4 } from 'uuid';

const PurchaseForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isEditMode = Boolean(id);
  const documentTypesRef = useRef(null);
  // Create a ref to store the purchase number once generated
  const purchaseNumberRef = useRef('');
  
  const { 
    addPurchase, 
    updatePurchase, 
    getPurchaseById, 
    getProducts, 
    getSuppliers, 
    getWarehouses,
    fetchAllData 
  } = useAppData();
  
  // Get document types and documents contexts
  const { documentTypes, getDocumentTypesByCategory, generateDocumentNumber } = useDocumentTypes();
  const { documents, refreshDocuments, createDocument } = useDocuments();
  
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [showSupplierSearch, setShowSupplierSearch] = useState(false);
  const [searchSupplierQuery, setSearchSupplierQuery] = useState('');
  const [supplierSearchMode, setSupplierSearchMode] = useState('list');
  const [isSearching, setIsSearching] = useState(false);
  const [addMode, setAddMode] = useState('product'); // 'product' | 'service'
  const [serviceName, setServiceName] = useState('');
  const [servicePrice, setServicePrice] = useState('');
  const [serviceQty, setServiceQty] = useState(1);
  
  const [purchaseData, setPurchaseData] = useState({
    purchaseNumber: '',
    date: new Date().toISOString().split('T')[0],
    supplierId: '',
    warehouseId: '', // Ajouter l'entrepôt de destination
    reference: '',
    documentType: 'bon-commande',
    type: 'products',
    paymentMethod: 'virement',
    paymentStatus: 'pending',
    status: 'ordered',
    dueDate: '',
    notes: '',
    items: []
  });
  
  // Données pour les fournisseurs, produits et entrepôts
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  
  // Charger les données des fournisseurs, produits et entrepôts (en temps réel)
  const getEntityName = (ent) => ent?.name || '';
  useEffect(() => {
    const s = getSuppliers();
    const sorted = [...s].sort((a,b) => getEntityName(a).toLowerCase().localeCompare(getEntityName(b).toLowerCase()));
    setSuppliers(sorted);
    setProducts(getProducts());
    setWarehouses(getWarehouses());
  }, [getSuppliers, getProducts, getWarehouses]);

  // Cache document types when they change for future reference
  useEffect(() => {
    documentTypesRef.current = documentTypes;
    console.log('Document types updated in purchase form:', documentTypes);
  }, [documentTypes]);
  
  // Effect to refresh document types when documents change
  useEffect(() => {
    if (documents && documents.length > 0) {
      console.log('Documents updated, document types may need refresh');
    }
  }, [documents]);
  
  // Generate purchase number based on document type - only once when component mounts
  useEffect(() => {
    // Generate purchase number automatically for new purchases using our shared document number generator
    if (!isEditMode) {
      // Make sure we use a valid document type
      let documentType = purchaseData.documentType;
      
      // Ensure the document type is one of the valid purchase types from DocumentCreation.js
      const validPurchaseTypes = ['purchase-order', 'reception-note'];
      if (!validPurchaseTypes.includes(documentType)) {
        documentType = 'purchase-order'; // Default to purchase order if invalid
      }
      
      // Only generate a purchase number if we haven't already
      if (!purchaseNumberRef.current) {
        const purchaseNumber = generateDocumentNumber(documentType);
        purchaseNumberRef.current = purchaseNumber; // Store in ref for stability
        
        setPurchaseData(prev => ({
          ...prev,
          documentType, // Ensure the document type is also updated if it was invalid
          purchaseNumber
        }));
        
        console.log("Initial purchase number generated:", purchaseNumber);
      }
    }
  }, [isEditMode, generateDocumentNumber]); // Remove purchaseData.documentType dependency

  useEffect(() => {
    // Si en mode édition, charger les données de l'achat
    if (isEditMode) {
      setIsLoading(true);
      
      try {
        // Récupérer l'achat par ID
        const existingPurchase = getPurchaseById(id);
        
        if (existingPurchase) {
          setPurchaseData(existingPurchase);
        } else {
          toast.error('Achat introuvable');
          navigate('/purchases');
        }
      } catch (error) {
        console.error('Erreur lors du chargement de l\'achat:', error);
        toast.error('Erreur lors du chargement de l\'achat');
        navigate('/purchases');
      } finally {
        setIsLoading(false);
      }
    }
  }, [isEditMode, id, getPurchaseById, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for document type to preserve purchase number
    if (name === 'documentType') {
      console.log(`Document type changing to: ${value}`);
      setPurchaseData(prev => ({
        ...prev,
        [name]: value,
        // Ensure we keep the original purchase number
        purchaseNumber: purchaseNumberRef.current || prev.purchaseNumber
      }));
    } else {
      setPurchaseData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleAddItem = () => {
    if (addMode === 'product') {
    setShowProductSearch(true);
    }
  };

  const handleSelectProduct = (product) => {
    const newItem = {
      id: product.id,
      name: product.name,
      sku: product.sku,
      quantity: 1,
      price: product.price,
      total: product.price,
      unit: product.unit
    };
    
    setPurchaseData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
    
    setShowProductSearch(false);
    setSearchQuery('');
    calculateTotals();
  };

  const handleRemoveItem = (index) => {
    setPurchaseData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
    calculateTotals();
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...purchaseData.items];
    
    if (field === 'quantity' || field === 'price') {
      value = Number(value);
      updatedItems[index][field] = value;
      updatedItems[index].total = updatedItems[index].quantity * updatedItems[index].price;
    } else {
      updatedItems[index][field] = value;
    }
    
    setPurchaseData(prev => ({
      ...prev,
      items: updatedItems
    }));
    
    calculateTotals();
  };

  const calculateTotals = () => {
    // Cette fonction sera appelée pour calculer les totaux
    // Dans une application réelle, elle mettrait à jour les sous-totaux, taxes, etc.
  };

  const filteredProducts = searchQuery.trim() === '' 
    ? [] 
    : products.filter(product => 
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.sku.toLowerCase().includes(searchQuery.toLowerCase())
      );
      
  const filteredSuppliers = searchSupplierQuery.trim() === ''
    ? []
    : suppliers.filter(supplier => {
        const query = searchSupplierQuery.toLowerCase();
        const name = supplier.name ? supplier.name.toLowerCase() : '';
        const email = supplier.email ? supplier.email.toLowerCase() : '';
        const phone = supplier.phone ? supplier.phone.toLowerCase() : '';
        return name.includes(query) || email.includes(query) || phone.includes(query);
      });
      
  const handleSelectSupplier = (supplier) => {
    setPurchaseData(prev => ({
      ...prev,
      supplierId: supplier.id,
      supplierName: supplier.name
    }));
    setShowSupplierSearch(false);
    setSearchSupplierQuery('');
  };

  // This effect runs when the document type changes to ensure document integration
  useEffect(() => {
    if (purchaseData.documentType && !isEditMode) {
      // If document type changes, log the change but don't update the purchase number
      const docType = getDocumentTypesByCategory('purchase').find(t => t.id === purchaseData.documentType);
      console.log(`Document type changed to: ${docType?.name || purchaseData.documentType}`);
      
      // Look for matching document templates from the documents system
      const matchingDocuments = documents?.filter(doc => doc.type === purchaseData.documentType);
      if (matchingDocuments && matchingDocuments.length > 0) {
        console.log(`Found ${matchingDocuments.length} matching documents of this type for reference`);
        
        // Could potentially use templates from these documents
        // For example, auto-fill certain fields based on document templates
      }
      
      // If we have a stored purchase number in our ref, make sure it's still set in the state
      // This ensures the purchase number remains stable even if state is updated for other reasons
      if (purchaseNumberRef.current && purchaseData.purchaseNumber !== purchaseNumberRef.current) {
        console.log("Restoring purchase number from ref:", purchaseNumberRef.current);
        setPurchaseData(prev => ({
          ...prev,
          purchaseNumber: purchaseNumberRef.current
        }));
      }
    }
  }, [purchaseData.documentType, isEditMode, getDocumentTypesByCategory, documents, purchaseData.purchaseNumber]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Refresh document types before submission to ensure we have the latest
    try {
      if (refreshDocuments) {
        await refreshDocuments();
      }
    } catch (err) {
      console.error("Couldn't refresh documents before submission", err);
    }
    
    // Validation basique
    if (!purchaseData.supplierId) {
      toast.error('Veuillez sélectionner un fournisseur');
      return;
    }
    
    if (!purchaseData.warehouseId) {
      toast.error('Veuillez sélectionner un entrepôt de destination');
      return;
    }
    
    if (purchaseData.items.length === 0) {
      toast.error('Veuillez ajouter au moins un article à l\'achat');
      return;
    }
    
    setIsLoading(true);
    
    try {
      let savedPurchase;
      
      if (isEditMode) {
        // Mise à jour d'un achat existant
        savedPurchase = await updatePurchase(purchaseData.id, purchaseData);
        toast.success(`L'achat ${purchaseData.purchaseNumber} a été mis à jour`);
      } else {
        // Création d'un nouvel achat
        savedPurchase = await addPurchase(purchaseData);
        toast.success(`L'achat ${purchaseData.purchaseNumber} a été créé avec succès`);
        
        // Create corresponding document in documents page
        await createCorrespondingDocument(savedPurchase);
      }
      
      // Rafraîchir les données pour s'assurer que l'UI est à jour
      await fetchAllData();
      
      // Navigation vers la liste des achats
      navigate('/purchases');
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement de l\'achat:', error);
      toast.error('Erreur lors de l\'enregistrement de l\'achat');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to create a corresponding document in the documents page
  const createCorrespondingDocument = async (purchase) => {
    try {
      if (!purchase) {
        console.error("Cannot create document: Purchase data is missing");
        return null;
      }
      
      // Get supplier details
      const supplier = suppliers.find(s => s.id === purchase.supplierId);
      if (!supplier) {
        console.error("Cannot create document: Supplier not found");
        return null;
      }
      
      // Map purchase document type to document type
      const documentTypeMapping = {
        'purchase-order': 'purchase-order',
        'reception-note': 'reception-note'
      };
      
      const documentType = documentTypeMapping[purchase.documentType] || 'purchase-order';
      
      // Create document data from purchase data
      const documentData = {
        type: documentType,
        number: purchase.purchaseNumber,
        date: purchase.date,
        dueDate: purchase.dueDate || null,
        supplierId: purchase.supplierId,
        contactName: supplier.name,
        contactEmail: supplier.email || '',
        contactPhone: supplier.phone || '',
        contactAddress: supplier.address || '',
        items: purchase.items.map(item => ({
          productId: item.id || null,
          description: item.name || item.description || '',
          quantity: parseInt(item.quantity) || 1,
          unitPrice: parseFloat(item.price) || 0,
          total: parseFloat(item.total) || 0
        })),
        subtotal: calculateSubtotal(purchase.items),
        taxRate: 19,
        taxAmount: calculateTaxAmount(purchase.items),
        total: calculateTotal(purchase.items),
        notes: purchase.notes || null,
        paymentMethod: purchase.paymentMethod || null,
        status: 'draft'
      };
      
      console.log("Creating corresponding document:", documentData);
      
      // Create the document
      const createdDocument = await createDocument(documentData);
      
      if (createdDocument) {
        toast.success("Document correspondant créé avec succès dans la section Documents");
        return createdDocument;
      } else {
        console.error("Failed to create corresponding document");
        return null;
      }
    } catch (error) {
      console.error("Error creating corresponding document:", error);
      toast.error("Erreur lors de la création du document correspondant");
      return null;
    }
  };
  
  // Helper functions for calculating totals
  const calculateSubtotal = (items) => {
    return items.reduce((sum, item) => {
      const itemTotal = parseFloat(item.total) || (parseFloat(item.price || 0) * parseInt(item.quantity || 0));
      return sum + (isNaN(itemTotal) ? 0 : itemTotal);
    }, 0);
  };
  
  const calculateTaxAmount = (items) => {
    const subtotal = calculateSubtotal(items);
    return subtotal * 0.19; // 19% tax rate
  };
  
  const calculateTotal = (items) => {
    const subtotal = calculateSubtotal(items);
    const taxAmount = calculateTaxAmount(items);
    return subtotal + taxAmount;
  };
  
  // Calculer le total de l'achat
  const totalAmount = purchaseData.items.reduce((sum, item) => {
    const q = Number(item.quantity ?? 0);
    const p = Number(item.price ?? 0);
    const line = Number(item.total ?? (q * p));
    return sum + (isNaN(line) ? 0 : line);
  }, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/purchases')}
            className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="h-6 w-6 text-white" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white">
              {isEditMode ? 'Modifier l\'achat' : 'Nouvel achat'}
            </h1>
            <p className="text-white/60 mt-1">
              {isEditMode ? 'Modifier les informations de l\'achat' : 'Créez un nouvel achat dans votre système'}
            </p>
          </div>
        </div>
      </div>

      {/* Formulaire */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Informations d'en-tête */}
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <h2 className="text-xl font-semibold text-white mb-6">Informations de l'achat</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="purchaseNumber" className="block text-white font-medium mb-1">
                    Numéro d'achat
                  </label>
                  <input
                    type="text"
                    id="purchaseNumber"
                    name="purchaseNumber"
                    value={purchaseData.purchaseNumber}
                    onChange={handleInputChange}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Généré automatiquement"
                    readOnly
                  />
                </div>
                
                <div>
                  <label htmlFor="date" className="block text-white font-medium mb-1">
                    Date d'achat <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <CalendarDaysIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                    <input
                      type="date"
                      id="date"
                      name="date"
                      value={purchaseData.date}
                      onChange={handleInputChange}
                      className="w-full pl-10 p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="supplierId" className="block text-white font-medium mb-1">
                    Fournisseur <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <BuildingOfficeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                    <select
                      id="supplierId"
                      name="supplierId"
                      value={purchaseData.supplierId}
                      onChange={(e) => {
                        const selected = suppliers.find(s => String(s.id) === e.target.value);
                        setPurchaseData(prev => ({ ...prev, supplierId: e.target.value, supplierName: selected ? selected.name : '' }));
                      }}
                      className="w-full pl-10 p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">-- Sélectionner un fournisseur --</option>
                      {suppliers.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div>
                  <label htmlFor="warehouseId" className="block text-white font-medium mb-1">
                    Entrepôt de destination <span className="text-red-400">*</span>
                  </label>
                  <select
                    id="warehouseId"
                    name="warehouseId"
                    value={purchaseData.warehouseId}
                    onChange={handleInputChange}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">-- Sélectionner un entrepôt --</option>
                    {warehouses.map(warehouse => (
                      <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label htmlFor="reference" className="block text-white font-medium mb-1">
                    Référence externe
                  </label>
                  <input
                    type="text"
                    id="reference"
                    name="reference"
                    value={purchaseData.reference}
                    onChange={handleInputChange}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: N° Bon de commande fournisseur"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                <div>
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <label htmlFor="documentType" className="block text-white font-medium">
                        Type de document
                      </label>
                      <button
                        type="button"
                        onClick={() => refreshDocuments && refreshDocuments()}
                        title="Actualiser les types de documents"
                        className="text-white/60 hover:text-white p-1 rounded-full hover:bg-white/10 transition-colors"
                      >
                        <ArrowPathIcon className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="relative">
                      <select
                        id="documentType"
                        name="documentType"
                        value={purchaseData.documentType}
                        onChange={(e) => {
                          handleInputChange(e);
                          // Document type changes are now handled in the useEffect
                        }}
                        className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {getDocumentTypesByCategory('purchase')?.length > 0 ? (
                          getDocumentTypesByCategory('purchase').map(docType => (
                            <option key={docType.id} value={docType.id}>
                              {docType.name}
                            </option>
                          ))
                        ) : (
                          <>
                            <option value="purchase-order">Bon de commande</option>
                            <option value="reception-note">Bon de réception</option>
                          </>
                        )}
                      </select>
                      {documentTypes?.length === 0 && (
                        <span className="absolute right-10 top-1/2 transform -translate-y-1/2 text-yellow-500 text-xs">
                          Chargement...
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div>
                  <label htmlFor="type" className="block text-white font-medium mb-1">
                    Type d'achat (catégorie)
                  </label>
                  <select
                    id="type"
                    name="type"
                    value={purchaseData.type}
                    onChange={handleInputChange}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="products">Produits</option>
                    <option value="equipment">Équipement</option>
                    <option value="services">Services</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="paymentMethod" className="block text-white font-medium mb-1">
                    Méthode de paiement
                  </label>
                  <select
                    id="paymentMethod"
                    name="paymentMethod"
                    value={purchaseData.paymentMethod}
                    onChange={handleInputChange}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="especes">Espèces</option>
                    <option value="cheque">Chèque</option>
                    <option value="virement">Virement bancaire</option>
                    <option value="carte">Carte bancaire</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="paymentStatus" className="block text-white font-medium mb-1">
                    Statut du paiement
                  </label>
                  <select
                    id="paymentStatus"
                    name="paymentStatus"
                    value={purchaseData.paymentStatus}
                    onChange={handleInputChange}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="pending">En attente</option>
                    <option value="partial">Partiel</option>
                    <option value="paid">Payé</option>
                    <option value="cancelled">Annulé</option>
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div>
                  <label htmlFor="status" className="block text-white font-medium mb-1">
                    Statut de la commande
                  </label>
                  <select
                    id="status"
                    name="status"
                    value={purchaseData.status}
                    onChange={handleInputChange}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="ordered">Commandé</option>
                    <option value="partial">Partiellement reçu</option>
                    <option value="received">Reçu</option>
                    <option value="cancelled">Annulé</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="dueDate" className="block text-white font-medium mb-1">
                    Date d'échéance
                  </label>
                  <input
                    type="date"
                    id="dueDate"
                    name="dueDate"
                    value={purchaseData.dueDate}
                    onChange={handleInputChange}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Produits */}
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-white">Articles</h2>
                
                <div className="flex items-center space-x-2">
                  <div className="bg-white/10 rounded-lg p-1 flex">
                    <button
                      type="button"
                      onClick={() => setAddMode('product')}
                      className={`px-3 py-1 rounded-md text-sm ${addMode==='product' ? 'bg-blue-500/40 text-white' : 'text-white/80 hover:bg-white/10'}`}
                    >Produit</button>
                    <button
                      type="button"
                      onClick={() => setAddMode('service')}
                      className={`px-3 py-1 rounded-md text-sm ${addMode==='service' ? 'bg-blue-500/40 text:white' : 'text-white/80 hover:bg-white/10'}`}
                    >Service</button>
                  </div>
                  {addMode === 'product' && (
                <button
                  type="button"
                  onClick={handleAddItem}
                  className="bg-white/10 hover:bg-white/20 transition-colors py-2 px-4 rounded-lg text-white flex items-center space-x-2"
                >
                  <PlusIcon className="h-5 w-5" />
                      <span>Ajouter un produit</span>
                </button>
                  )}
                </div>
              </div>
              
              {addMode === 'product' && showProductSearch && (
                <div className="mb-6 bg-white/5 rounded-xl p-4 border border-white/10">
                  <div className="flex items-center mb-4">
                    <h3 className="text-white font-medium">Rechercher un produit</h3>
                    <button
                      type="button"
                      onClick={() => setShowProductSearch(false)}
                      className="ml-auto p-1.5 bg-white/10 rounded-lg hover:bg-white/20 text-white"
                    >
                      <XCircleIcon className="h-5 w-5" />
                    </button>
                  </div>
                  
                  <div className="relative mb-4">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Rechercher par nom ou référence"
                      className="w-full pl-10 p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  {filteredProducts.length > 0 ? (
                    <div className="max-h-64 overflow-y-auto bg-white/5 rounded-lg">
                      <table className="min-w-full divide-y divide-white/10">
                        <thead className="bg-white/5">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-white/70">Référence</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-white/70">Produit</th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-white/70">Prix</th>
                            <th className="px-4 py-2 text-center text-xs font-medium text-white/70">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredProducts.map((product) => (
                            <tr key={product.id} className="hover:bg-white/10">
                              <td className="px-4 py-2 text-sm text-white/80">{product.sku}</td>
                              <td className="px-4 py-2 text-sm text-white">{product.name}</td>
                              <td className="px-4 py-2 text-sm text-white text-right">{product.price.toLocaleString()} DA</td>
                              <td className="px-4 py-2 text-center">
                                <button
                                  type="button"
                                  onClick={() => handleSelectProduct(product)}
                                  className="p-1.5 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg text-blue-400"
                                >
                                  <PlusIcon className="h-4 w-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : searchQuery.trim() !== '' ? (
                    <p className="text-white/60 text-center py-4">Aucun produit trouvé</p>
                  ) : null}
                </div>
              )}

              {addMode === 'service' && (
                <div className="mb-6 bg-white/5 rounded-xl p-4 border border-white/10">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm text-white/80 mb-1">Intitulé du service</label>
                      <input
                        type="text"
                        value={serviceName}
                        onChange={(e)=>setServiceName(e.target.value)}
                        placeholder="Ex: Maintenance, Installation..."
                        className="w-full p-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-white/80 mb-1">Prix</label>
                      <input
                        type="number"
                        min="0"
                        value={servicePrice}
                        onChange={(e)=>setServicePrice(e.target.value)}
                        className="w-full p-2 bg-white/10 border border-white/20 rounded-lg text-white text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-white/80 mb-1">Quantité</label>
                      <input
                        type="number"
                        min="1"
                        value={serviceQty}
                        onChange={(e)=>setServiceQty(Number(e.target.value) || 1)}
                        className="w-full p-2 bg-white/10 border border-white/20 rounded-lg text-white text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        if (!serviceName.trim()) { toast.error('Veuillez saisir le nom du service'); return; }
                        const priceNum = Number(servicePrice); const qtyNum = Number(serviceQty);
                        const newItem = {
                          id: `SRV-${Date.now()}`,
                          name: serviceName.trim(),
                          sku: '',
                          quantity: isNaN(qtyNum) ? 1 : qtyNum,
                          price: isNaN(priceNum) ? 0 : priceNum,
                          total: (isNaN(priceNum) ? 0 : priceNum) * (isNaN(qtyNum) ? 1 : qtyNum),
                          unit: 'service',
                          itemType: 'service'
                        };
                        setPurchaseData(prev => ({ ...prev, items: [...prev.items, newItem] }));
                        setServiceName(''); setServicePrice(''); setServiceQty(1);
                      }}
                      className="bg-white/10 hover:bg-white/20 transition-colors py-2 px-4 rounded-lg text-white flex items-center space-x-2"
                    >
                      <PlusIcon className="h-5 w-5" />
                      <span>Ajouter le service</span>
                    </button>
                  </div>
                </div>
              )}
              
              {purchaseData.items.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-white/10">
                    <thead className="bg-white/5">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white/70">Produit</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white/70">Référence</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-white/70">Quantité</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-white/70">Prix unitaire</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-white/70">Total</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-white/70">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10">
                      {purchaseData.items.map((item, index) => (
                        <tr key={index}>
                          <td className="px-4 py-3 text-sm text-white">{item.name}</td>
                          <td className="px-4 py-3 text-sm text-white/80">{item.sku || '—'}</td>
                          <td className="px-4 py-3 text-sm">
                            <input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                              className="w-20 p-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </td>
                          <td className="px-4 py-3 text-sm text-white text-right">
                            <input
                              type="number"
                              min="0"
                              value={item.price}
                              onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                              className="w-28 p-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-right focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                            <span className="ml-1">DA</span>
                          </td>
                          <td className="px-4 py-3 text-sm text-white font-medium text-right">{item.total.toLocaleString()} DA</td>
                          <td className="px-4 py-3 text-center">
                            <button
                              type="button"
                              onClick={() => handleRemoveItem(index)}
                              className="p-1.5 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400 transition-colors"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 border border-dashed border-white/20 rounded-xl">
                  <CurrencyDollarIcon className="h-12 w-12 mx-auto text-white/30 mb-2" />
                  <p className="text-white/60">Aucun article ajouté à cet achat.</p>
                  <p className="text-white/50 text-sm mt-1">Cliquez sur "Ajouter un article" pour commencer.</p>
                </div>
              )}

              {purchaseData.items.length > 0 && (
                <div className="mt-6 flex justify-end">
                  <div className="bg-white/5 rounded-xl p-4 w-64 space-y-3">
                    <div className="flex justify-between text-white/80">
                      <span>Sous-total:</span>
                      <span>{totalAmount.toLocaleString()} DA</span>
                    </div>
                    <div className="flex justify-between text-white font-bold border-t border-white/10 pt-3">
                      <span>Total:</span>
                      <span>{totalAmount.toLocaleString()} DA</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <h2 className="text-xl font-semibold text-white mb-4">Notes</h2>
              
              <textarea
                id="notes"
                name="notes"
                value={purchaseData.notes}
                onChange={handleInputChange}
                rows="4"
                className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Notes additionnelles sur cet achat..."
              ></textarea>
            </div>
            
            {/* Boutons d'action */}
            <div className="flex justify-end space-x-4 pt-4">
              <button
                type="button"
                onClick={() => navigate('/purchases')}
                className="px-6 py-3 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-colors flex items-center"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="animate-spin h-4 w-4 mr-2 border-b-2 border-white rounded-full"></span>
                    Traitement...
                  </>
                ) : isEditMode ? 'Mettre à jour' : 'Enregistrer'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default PurchaseForm; 